import { useEffect, useState } from 'react';

/**
 * T-0559: the launch screen's code, fetched at boot (`bootRoutesFor` in `App.tsx`), as something the
 * session gate can wait on.
 *
 * When the session check answered before that code arrived, the layout drew its "Loading page"
 * fallback, and React holds a revealed fallback for at least 300 ms: the screen the person launched
 * into came a third of a second late. So the gate's own spinner stays until both are in, and the
 * route draws straight from its chunk. Only the first load waits here; once the boot fetch has
 * settled, failed or not, nothing does.
 */
let pending: Promise<void> | null = null;
let settled = true;

/**
 * How long the gate waits on that code at most. A chunk that never arrives (a stalled network, a
 * deployment that stopped answering mid-launch) used to hold the gate's spinner for good: no
 * navigation, no screen, no failure (await-view's held chunk, gate 474a). Past this, the layout draws
 * and the route shows its own fallback, as it did before T-0559.
 */
export const BOOT_ROUTES_MAX_WAIT_MS = 1_500;

export function holdForBootRoutes(
  fetches: Promise<unknown>[],
  maxWaitMs: number = BOOT_ROUTES_MAX_WAIT_MS,
): void {
  if (fetches.length === 0) return;
  settled = false;
  const all = Promise.race([
    Promise.allSettled(fetches),
    new Promise((resolve) => setTimeout(resolve, maxWaitMs)),
  ]).then(() => {
    settled = true;
  });
  pending = all;
}

/** Whether the boot fetch of the launch screen's code has settled. */
export function useBootRoutesSettled(): boolean {
  const [done, setDone] = useState(settled);
  useEffect(() => {
    if (done || !pending) return undefined;
    let live = true;
    void pending.then(() => {
      if (live) setDone(true);
    });
    return () => {
      live = false;
    };
  }, [done]);
  return done || settled;
}

/**
 * T-0559: a route's handle may name the first data its screen reads, so a cold load of it can start
 * that read beside the session check (`bootRoutesFor`). The read is handed to the screen's own first
 * ask (`services/bootPrefetch.ts`).
 */
export interface IBootDataHandle {
  bootData: () => void;
}

export function bootDataOf(handle: unknown): (() => void) | null {
  if (typeof handle !== 'object' || handle === null) return null;
  const bootData = (handle as Partial<IBootDataHandle>).bootData;
  return typeof bootData === 'function' ? bootData : null;
}
